export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface FormValidationRules {
  name: {
    minLength: number;
    maxLength: number;
  };
  whatsapp: {
    minLength: number;
  };
  cnpj: {
    length: number;
  };
}

// Regras de validação simplificadas
export const VALIDATION_RULES: FormValidationRules = {
  name: {
    minLength: 2,
    maxLength: 100
  },
  whatsapp: {
    minLength: 10
  },
  cnpj: {
    length: 14
  }
};

/**
 * Formata número de WhatsApp para padrão brasileiro
 */
export function formatWhatsApp(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 7) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  if (cleaned.length <= 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  
  // Para números com DDD + 9 dígitos
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
}

/**
 * Formata CNPJ para padrão brasileiro
 */
export function formatCNPJ(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
  if (cleaned.length <= 12) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
  }
  
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`;
}

/**
 * Valida campo de nome - apenas verifica se não está vazio
 */
export function validateName(name: string): ValidationResult {
  const errors: string[] = [];
  const trimmedName = name.trim();
  
  if (!trimmedName) {
    errors.push('Nome é obrigatório');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Valida campo de WhatsApp - apenas verifica se não está vazio e tem dígitos suficientes
 */
export function validateWhatsApp(whatsapp: string): ValidationResult {
  const errors: string[] = [];
  const cleaned = whatsapp.replace(/\D/g, '');
  
  if (!whatsapp.trim()) {
    errors.push('WhatsApp é obrigatório');
  } else if (cleaned.length < VALIDATION_RULES.whatsapp.minLength) {
    errors.push('WhatsApp deve ter pelo menos 10 dígitos');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Valida campo de CNPJ - apenas verifica se não está vazio e tem 14 dígitos
 */
export function validateCNPJ(cnpj: string): ValidationResult {
  const errors: string[] = [];
  const cleaned = cnpj.replace(/\D/g, '');
  
  if (!cnpj.trim()) {
    errors.push('CNPJ é obrigatório');
  } else if (cleaned.length !== VALIDATION_RULES.cnpj.length) {
    errors.push('CNPJ deve ter 14 dígitos');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Valida todo o formulário
 */
export function validateLeadForm(formData: { name: string; whatsapp: string; cnpj?: string }): ValidationResult {
  const nameValidation = validateName(formData.name);
  const whatsappValidation = validateWhatsApp(formData.whatsapp);
  
  const allErrors = [
    ...nameValidation.errors,
    ...whatsappValidation.errors
  ];
  
  if (formData.cnpj) {
    const cnpjValidation = validateCNPJ(formData.cnpj);
    allErrors.push(...cnpjValidation.errors);
  }
  
  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  };
}

/**
 * Sanitiza dados do formulário removendo caracteres perigosos
 */
export function sanitizeFormData(formData: { name: string; whatsapp: string; cnpj?: string }) {
  return {
    name: formData.name.trim(),
    whatsapp: formatWhatsApp(formData.whatsapp),
    ...(formData.cnpj ? { cnpj: formatCNPJ(formData.cnpj) } : {})
  };
}